// ─── Modal Type ───────────────────────────────────────────────────────────────

/**
 * Semantic type for a modal surface.
 *
 * - dialog  : centered overlay modal (most common ERP use)
 * - drawer  : slides in from a side (right/left)
 * - confirm : small blocking confirmation dialog
 * - sheet   : bottom sheet (mobile-style)
 * - popover : anchored non-modal popup (autocomplete, date picker)
 */
export type ModalType = 'dialog' | 'drawer' | 'confirm' | 'sheet' | 'popover';

// ─── Close Guard ──────────────────────────────────────────────────────────────

/**
 * A function called before the modal is closed by a user action (Esc,
 * backdrop click, or close button). Return `true` to allow close, `false`
 * to block it.
 *
 * Async guards are supported — useful when you need to `await` a confirmation
 * dialog before allowing close.
 *
 * `event.preventDefault()` is always called BEFORE the guard runs, so the
 * default Esc browser behavior is always suppressed.
 *
 * @example
 * // Block close when form is dirty
 * const guard: CloseGuard = () => {
 *   if (!isDirty) return true;
 *   showDirtyConfirm();   // consumer shows their own confirm modal
 *   return false;         // block the initiating close
 * };
 */
export type CloseGuard = () => boolean | Promise<boolean>;

// ─── Modal Entry ──────────────────────────────────────────────────────────────

/**
 * The full state of a registered open modal, stored in ModalRegistry._stack.
 *
 * Mutable fields (`closeGuard`) allow reactive updates without re-opening the
 * modal (e.g. when dirty state changes after the modal is already open).
 */
export interface ModalEntry {
  /** Unique ID for this modal instance. */
  readonly id: string;
  /** Semantic type — drives default behaviour. */
  readonly type: ModalType;
  /**
   * Guard called when user attempts to close (Esc, backdrop, close button).
   * Mutable — updated via `registry.setCloseGuard()` or `useCloseGuard()`.
   */
  closeGuard: CloseGuard | null;
  /**
   * Called after the guard passes and the modal is actually removed from the
   * stack. Use this to set your `isOpen` state to `false`.
   */
  readonly onClose: (() => void) | undefined;
  /** Whether Esc triggers a close attempt. Default: true (except 'popover'). */
  readonly closeOnEscape: boolean;
  /** Whether clicking the backdrop/overlay triggers a close attempt. Default: false. */
  readonly closeOnOverlayClick: boolean;
  /** Optional accessible label (for debug and aria-labelledby coordination). */
  readonly title: string | undefined;
  /** Timestamp (Date.now()) when this modal was opened. */
  readonly openedAt: number;
}

// ─── Modal Options ────────────────────────────────────────────────────────────

/**
 * Input shape accepted by `registry.open()` and `useControlledModal`.
 */
export interface ModalOptions {
  /** Semantic type. Default: 'dialog'. */
  type?: ModalType;
  /**
   * Called when the modal should close (user-initiated via Esc, backdrop, or
   * close button AND the close guard passes).
   * Use this to set your controlled `isOpen` state to `false`.
   *
   * Not called by `registry.forceClose()` — that is for programmatic/sync close.
   */
  onClose?: () => void;
  /**
   * Initial close guard. Can also be set/updated reactively via `useCloseGuard`.
   * If both are provided, `useCloseGuard` overwrites this after mount.
   */
  closeGuard?: CloseGuard;
  /** Default: true for dialog/drawer/confirm/sheet; false for popover. */
  closeOnEscape?: boolean;
  /** Default: false. */
  closeOnOverlayClick?: boolean;
  /** Optional human-readable label. */
  title?: string;
}

// ─── Z-Index ──────────────────────────────────────────────────────────────────

/** Base CSS z-index for the first modal in the stack. */
export const BASE_MODAL_Z_INDEX = 1000;
/** z-index increment for each additional stacked modal. */
export const MODAL_Z_INDEX_STEP = 10;

// ─── Handles ─────────────────────────────────────────────────────────────────

/**
 * Returned by `useControlledModal`. The parent component controls `isOpen`.
 */
export interface ControlledModalHandle {
  /**
   * Computed CSS z-index for this modal based on stack position.
   * Apply to the modal's container element.
   */
  readonly zIndex: number;
  /** Whether this modal is at the top of the stack (receives Esc). */
  readonly isTopmost: boolean;
  /**
   * Attempt to close this modal via user intent.
   * Runs the close guard and calls `onClose` if allowed.
   * Returns `true` if the modal was closed, `false` if blocked by guard.
   */
  triggerClose: () => Promise<boolean>;
  /**
   * Update the close guard imperatively.
   * Prefer `useCloseGuard()` for reactive guard updates tied to component state.
   */
  setCloseGuard: (guard: CloseGuard | null) => void;
}

/**
 * Returned by `useModal`. The hook manages `isOpen` internally.
 */
export interface UncontrolledModalHandle extends ControlledModalHandle {
  /** Whether this modal is currently open. */
  readonly isOpen: boolean;
  /** Open this modal. */
  open: () => void;
  /**
   * Attempt to close this modal via user intent.
   * Runs the close guard. Closes if guard passes.
   */
  close: () => Promise<boolean>;
  /**
   * Immediately close without running the close guard.
   * Use for programmatic close (e.g. after successful form submit).
   */
  forceClose: () => void;
}

// ─── Change Listener ─────────────────────────────────────────────────────────

export type ModalChangeListener = () => void;
